import React from 'react';
import { Flex } from 'rebass';
import moment from 'moment';

import StatusLabel from 'components/StatusLabel';
import Progress from 'components/Progress';
import helpers from 'utils/helpers';
import {
    EMAIL_CAMPAIGN_STATUS_SENT,
    EMAIL_CAMPAIGN_STATUS_READY,
    EMAIL_CAMPAIGN_STATUS_SENDING,
    EMAIL_CAMPAIGN_STATUS_SCHEDULED,
    EMAIL_CAMPAIGN_STATUS_CANCELLED,
    SMS_STATUS_SENT,
    SMS_STATUS_FAILED
} from 'utils/constants';
import { isCampaignSending, getDisplayStatus, getCampaignProgress } from './utils';
import { DATE_STYLES, getMomentFormat } from 'utils/dateFormats';

/**
 * Maps campaign status to StatusLabel color
 * @param {string} status - Campaign status
 * @returns {string} Color name (green, orange, gray, danger)
 */
const getStatusColor = status => {
    switch (status) {
        case EMAIL_CAMPAIGN_STATUS_SENT:
        case EMAIL_CAMPAIGN_STATUS_READY:
        case EMAIL_CAMPAIGN_STATUS_SENDING:
        case SMS_STATUS_SENT:
            return 'green';
        case EMAIL_CAMPAIGN_STATUS_SCHEDULED:
            return 'orange';
        case EMAIL_CAMPAIGN_STATUS_CANCELLED:
            return 'gray';
        case SMS_STATUS_FAILED:
            return 'danger';
        default:
            return 'danger';
    }
};

/**
 * CampaignStatus component - Displays status and progress for email and SMS campaigns
 *
 * @param {Object} props
 * @param {'email' | 'sms'} [props.type='email'] - Campaign type
 * @param {Object} props.campaign - Campaign object
 * @param {string} props.campaign.status - Campaign status (sent, ready, scheduled, cancelled, failed)
 * @param {Object} [props.campaign.stats2] - Email campaign statistics (email only)
 * @param {number} [props.campaign.stats2.in_queue] - Number of emails still in queue (email only)
 * @param {number} [props.campaign.recipients_to_count] - Total recipient count (email only, for progress)
 * @param {string} [props.campaign.future_send_date] - ISO date for scheduled campaigns (email only)
 * @param {Function} [props.onCancelCampaign] - Callback for canceling scheduled campaigns (email only)
 * @param {Object} [props.style] - Optional inline styles
 * @param {string} [props.className] - Optional CSS class
 */
const CampaignStatus = ({ type = 'email', campaign, onCancelCampaign, style, className }) => {
    if (!campaign) return null;

    // SMS campaigns: simple status label only
    if (type === 'sms') {
        return (
            <StatusLabel
                status={helpers.capitalize(campaign.status)}
                color={getStatusColor(campaign.status)}
                style={style}
                className={className}
            />
        );
    }

    // Email campaigns: complex status with progress and scheduling
    const isSending = isCampaignSending(campaign);
    const displayStatus = getDisplayStatus(campaign);
    const progress = getCampaignProgress(campaign);

    return (
        <Flex style={{ flexDirection: 'column', gap: '2px', ...style }} className={className}>
            {/* Primary status label */}
            <StatusLabel status={helpers.capitalize(displayStatus)} color={getStatusColor(displayStatus)} />

            {/* Progress bar (only when sending) */}
            {isSending && progress && (
                <Progress value={(progress.sent / progress.total) * 100} style={{ margin: '4px 0' }} />
            )}

            {/* Scheduled campaign details */}
            {campaign.status === EMAIL_CAMPAIGN_STATUS_SCHEDULED && (
                <>
                    {campaign.future_send_date && (
                        <div className="fs-body-12 color-text-secondary">
                            {moment
                                .utc(campaign.future_send_date)
                                .local()
                                .format(getMomentFormat(DATE_STYLES.DATE_TIME))}
                        </div>
                    )}
                    {onCancelCampaign && (
                        <div
                            className="link-danger fs-body-12"
                            onClick={onCancelCampaign}
                            style={{ cursor: 'pointer' }}
                        >
                            Cancel Campaign
                        </div>
                    )}
                </>
            )}
        </Flex>
    );
};

export default CampaignStatus;

// Backward compatibility alias
export { CampaignStatus as EmailCampaignStatus };
